import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CalendarDays, Plus, Trash2, Pencil, Save, X, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ORDINAL = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const isoToDisplay = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '';
  return `${MONTHS[m - 1]} ${ORDINAL(d)}, ${y}`;
};

const EMPTY = { sort_date: '', event_date: '', venue_name: '', venue_url: '' };

/**
 * Event-schedule CRUD — now nested under Event Settings.
 * Manages the list of upcoming Repair Café event dates/venues.
 * The soonest future entry is auto-promoted to the public website.
 */
export default function EventScheduleSection() {
  const { getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => { fetchList(); }, []);

  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/event-settings/schedule`, { headers: getAuthHeader() });
      setItems(res.data || []);
    } catch {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const onPickDate = (iso) => {
    setForm((prev) => ({
      ...prev,
      sort_date: iso,
      event_date: prev.event_date && prev.event_date !== isoToDisplay(prev.sort_date) ? prev.event_date : isoToDisplay(iso),
    }));
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      sort_date: it.sort_date,
      event_date: it.event_date,
      venue_name: it.venue_name,
      venue_url: it.venue_url || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.sort_date) return toast.error('Please pick a calendar date');
    if (!form.event_date.trim()) return toast.error('Display date is required');
    if (!form.venue_name.trim()) return toast.error('Venue name is required');

    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/event-settings/schedule/${editingId}`, form, { headers: getAuthHeader() });
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/event-settings/schedule`, form, { headers: getAuthHeader() });
        toast.success('Event added to schedule');
      }
      cancelEdit();
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Remove ${label} from the schedule?`)) return;
    try {
      await axios.delete(`${API}/event-settings/schedule/${id}`, { headers: getAuthHeader() });
      toast.success('Event removed');
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="event-schedule-section" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold text-[#193460] flex items-center gap-2">
            <CalendarDays className="w-5 h-5" /> Upcoming Events
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            The soonest future entry is auto-published to the home page and booking form.
          </p>
        </div>
      </div>

      {/* Add / edit form */}
      <form onSubmit={submit} className="grid md:grid-cols-2 gap-4 p-4 border-2 border-gray-200 rounded-lg bg-white">
        <div className="md:col-span-2 text-sm font-medium text-[#193460] flex items-center gap-2">
          {editingId ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {editingId ? 'Edit scheduled event' : 'Add a new scheduled event'}
        </div>
        <div>
          <Label htmlFor="sort_date">Calendar date *</Label>
          <Input id="sort_date" type="date"
            data-testid="sched-date-input"
            value={form.sort_date}
            onChange={(e) => onPickDate(e.target.value)}
            required />
          <p className="text-xs text-gray-500 mt-1">Used for chronological order.</p>
        </div>
        <div>
          <Label htmlFor="event_date">Display text *</Label>
          <Input id="event_date"
            data-testid="sched-display-input"
            value={form.event_date}
            onChange={(e) => setForm({ ...form, event_date: e.target.value })}
            placeholder='e.g., "June 6th, 2026"'
            required />
        </div>
        <div>
          <Label htmlFor="venue_name">Venue *</Label>
          <Input id="venue_name"
            data-testid="sched-venue-input"
            value={form.venue_name}
            onChange={(e) => setForm({ ...form, venue_name: e.target.value })}
            placeholder="e.g., Railworld"
            required />
        </div>
        <div>
          <Label htmlFor="venue_url">Venue website URL</Label>
          <Input id="venue_url" type="url"
            data-testid="sched-url-input"
            value={form.venue_url}
            onChange={(e) => setForm({ ...form, venue_url: e.target.value })}
            placeholder="https://..." />
        </div>
        <div className="md:col-span-2 flex gap-2 justify-end">
          {editingId && (
            <Button type="button" variant="outline" onClick={cancelEdit}
              data-testid="sched-cancel-btn"
              className="border-gray-300">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
          )}
          <Button type="submit" disabled={saving} data-testid="sched-submit-btn"
            className="bg-[#E87B6A] hover:bg-[#d66a59] text-white">
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Saving...' : editingId ? 'Save changes' : 'Add event'}
          </Button>
        </div>
      </form>

      {/* Table */}
      {loading ? (
        <p className="text-center text-gray-500 py-6">Loading schedule...</p>
      ) : items.length === 0 ? (
        <div className="p-6 text-center border-2 border-dashed border-gray-300 rounded-lg">
          <CalendarDays className="w-8 h-8 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-600">No events scheduled yet. Add one above.</p>
        </div>
      ) : (
        <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px]" data-testid="schedule-table">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-700">Date</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-700">Venue</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-700">Website</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-700 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const isPast = it.sort_date < today;
                  return (
                    <tr key={it.id}
                      className={`border-t ${isPast ? 'bg-gray-50 text-gray-400' : 'hover:bg-orange-50/40'}`}
                      data-testid={`schedule-row-${i}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{it.event_date}</div>
                        <div className="text-xs text-gray-500">{it.sort_date}{isPast ? ' · past' : ''}</div>
                      </td>
                      <td className="px-4 py-3">{it.venue_name}</td>
                      <td className="px-4 py-3">
                        {it.venue_url ? (
                          <a href={it.venue_url} target="_blank" rel="noreferrer"
                            className="text-[#E87B6A] hover:underline inline-flex items-center gap-1 text-sm">
                            Visit <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : <span className="text-gray-400 text-sm">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => startEdit(it)}
                          data-testid={`sched-edit-${i}`}
                          className="border-[#193460] text-[#193460] hover:bg-[#193460] hover:text-white">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" onClick={() => remove(it.id, it.event_date)}
                          data-testid={`sched-delete-${i}`}
                          className="bg-red-600 hover:bg-red-700 text-white">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
